import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Tooltip, IconButton, TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  GetApp as DownloadIcon,
  HealthAndSafety as HealthIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import { format } from 'date-fns';
import { apiGet } from '../../services/apiService';

const severityColor = (sev) => {
  if (sev === 'alta') return 'error';
  if (sev === 'media') return 'warning';
  return 'info';
};

const severityIcon = (sev) => {
  if (sev === 'alta') return <ErrorIcon fontSize="small" />;
  if (sev === 'media') return <WarningIcon fontSize="small" />;
  return <InfoIcon fontSize="small" />;
};

// Celula expansivel para listas longas (ex: dias_faltantes)
const ExpandableListCell = ({ items, preview = 2 }) => {
  const [open, setOpen] = useState(false);
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return <span style={{ color: '#999' }}>—</span>;
  const isShort = arr.length <= preview;
  const visible = open || isShort ? arr : arr.slice(0, preview);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      {!isShort && (
        <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: 0, mt: '2px' }}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      )}
      <Box>
        <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
          {visible.join(', ')}
        </Typography>
        {!open && !isShort && (
          <Typography
            variant="caption"
            component="span"
            sx={{ ml: 0.5, color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
            onClick={() => setOpen(true)}
          >
            + {arr.length - preview} mais
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Decide como renderizar valor da celula
const renderCell = (value) => {
  if (Array.isArray(value)) {
    return <ExpandableListCell items={value} preview={2} />;
  }
  if (typeof value === 'number') return value.toLocaleString('pt-BR');
  return String(value ?? '');
};

const HealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  // Default: ultimos 14 dias
  const hoje = new Date();
  const inicioPadrao = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(inicioPadrao);
  const [endDate, setEndDate] = useState(hoje);

  const carregar = async () => {
    if (!startDate || !endDate) {
      setErro('Informe data inicial e data final');
      return;
    }
    if (endDate < startDate) {
      setErro('Data final precisa ser >= data inicial');
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const params = {
        data_inicio: format(startDate, 'yyyy-MM-dd'),
        data_fim: format(endDate, 'yyyy-MM-dd'),
      };
      const result = await apiGet('/health/redshift-ncs', params);
      setData(result);
    } catch (e) {
      setErro(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exporta texto formatado pra enviar ao time GPM
  const gerarTextoGPM = () => {
    if (!data) return '';
    const linhas = [];
    linhas.push(`Relatório de Não-Conformidades — Redshift`);
    linhas.push(`Período: ${data.periodo.inicio} a ${data.periodo.fim} (${data.periodo.dias} dias)`);
    linhas.push(`Gerado em: ${data.gerado_em}`);
    linhas.push(`Total de NCs: ${data.total_ncs}`);
    linhas.push('');
    for (const nc of data.ncs || []) {
      linhas.push(`──────────────────────────────────────────`);
      linhas.push(`[${(nc.severidade || '').toUpperCase()}] ${nc.tipo} — ${nc.tabela_redshift}`);
      linhas.push(`Título: ${nc.titulo}`);
      linhas.push(`Descrição: ${nc.descricao}`);
      if (typeof nc.valor_total === 'number') {
        linhas.push(`Valor afetado: R$ ${nc.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
      }
      if (Array.isArray(nc.detalhe) && nc.detalhe.length) {
        linhas.push(`Detalhes (top ${Math.min(nc.detalhe.length, 10)}):`);
        nc.detalhe.slice(0, 10).forEach((d, i) => {
          linhas.push(`  ${i + 1}. ${JSON.stringify(d)}`);
        });
      } else if (nc.detalhe && typeof nc.detalhe === 'object') {
        linhas.push(`Detalhe: ${JSON.stringify(nc.detalhe)}`);
      }
      linhas.push('');
    }
    return linhas.join('\n');
  };

  const copiarTexto = async () => {
    try {
      await navigator.clipboard.writeText(gerarTextoGPM());
      alert('Relatório copiado pra área de transferência. Pode colar no e-mail/Slack pro time GPM.');
    } catch (e) {
      alert('Erro ao copiar: ' + e.message);
    }
  };

  const baixarTexto = () => {
    const blob = new Blob([gerarTextoGPM()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `redshift-ncs-${data?.periodo?.inicio}_${data?.periodo?.fim}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Health — Não-Conformidades do Redshift</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label="Data inicial"
            value={startDate}
            onChange={(d) => setStartDate(d)}
            inputFormat="dd/MM/yyyy"
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: 160 }} />
            )}
          />
          <DatePicker
            label="Data final"
            value={endDate}
            onChange={(d) => setEndDate(d)}
            inputFormat="dd/MM/yyyy"
            minDate={startDate || undefined}
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: 160 }} />
            )}
          />
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={carregar} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Copiar relatório pra enviar ao GPM">
            <span>
              <IconButton onClick={copiarTexto} disabled={!data}>
                <CopyIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Baixar TXT">
            <span>
              <IconButton onClick={baixarTexto} disabled={!data}>
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {erro && <Alert severity="error" sx={{ mb: 2 }}>Erro ao carregar: {erro}</Alert>}

      {data && !loading && (
        <>
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(0, 204, 136, 0.05)' }}>
            <Typography variant="body2" color="text.secondary">
              Período analisado: <strong>{data.periodo.inicio}</strong> a <strong>{data.periodo.fim}</strong>
              {' '}({data.periodo.dias} dias) • {data.total_ncs} NCs detectadas
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gerado em: {new Date(data.gerado_em).toLocaleString('pt-BR')}
            </Typography>
          </Paper>

          {data.total_ncs === 0 && (
            <Alert severity="success">Nenhuma não-conformidade detectada no período! 🎉</Alert>
          )}

          {(data.ncs || []).map((nc, idx) => (
            <Accordion key={idx} defaultExpanded={nc.severidade === 'alta'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip
                    icon={severityIcon(nc.severidade)}
                    label={(nc.severidade || '').toUpperCase()}
                    color={severityColor(nc.severidade)}
                    size="small"
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{nc.titulo}</Typography>
                  <Chip label={nc.tipo} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {nc.tabela_redshift}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip label={`${nc.count ?? '?'} itens`} size="small" />
                  {typeof nc.valor_total === 'number' && (
                    <Chip
                      label={`R$ ${nc.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                      size="small" color="warning"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2 }}>{nc.descricao}</Typography>

                {Array.isArray(nc.detalhe) && nc.detalhe.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {Object.keys(nc.detalhe[0]).map((k) => (
                            <TableCell key={k} sx={{ fontWeight: 600 }}>{k}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nc.detalhe.slice(0, 50).map((row, i) => (
                          <TableRow key={i}>
                            {Object.values(row).map((v, j) => (
                              <TableCell key={j} sx={{ verticalAlign: 'top' }}>
                                {renderCell(v)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {nc.detalhe && typeof nc.detalhe === 'object' && !Array.isArray(nc.detalhe) && (
                  <Paper sx={{ p: 1, fontFamily: 'monospace', fontSize: '0.85em' }} variant="outlined">
                    <pre style={{ margin: 0 }}>{JSON.stringify(nc.detalhe, null, 2)}</pre>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default HealthPage;
